import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { toast } from "sonner";
import { Check, Truck, CreditCard } from "lucide-react";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [myTier, setMyTier] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const [paypalPlan, setPaypalPlan] = useState(null);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user?.role === "owner") api.get("/my/truck").then((r) => setMyTier(r.data?.tier || null)).catch(() => {});
  }, [user]);

  const upgrade = async (planKey) => {
    if (!user) { toast.error("Log in as a truck owner to upgrade"); navigate("/register"); return; }
    if (user.role !== "owner") { toast.error("Only food truck owners can subscribe"); return; }
    setLoadingKey(planKey);
    try {
      const { data } = await api.post("/payments/checkout", { plan: planKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setLoadingKey(null); }
  };

  const openPayPal = (planKey) => {
    if (!user) { toast.error("Log in as a truck owner to upgrade"); navigate("/register"); return; }
    if (user.role !== "owner") { toast.error("Only food truck owners can subscribe"); return; }
    setPaypalPlan(planKey);
  };

  const onPaypalSuccess = (planKey) => {
    setMyTier(planKey);
    setPaypalPlan(null);
    navigate("/dashboard/owner");
  };

  if (!plans) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-4 border-teal-200 border-t-[#0FB5A6] animate-spin" /></div>;

  const cards = [
    { key: "basic", ...plans.basic, cta: "Start free", highlight: false },
    ...plans.plans.map((p) => ({ ...p, cta: `Upgrade to ${p.name}`, highlight: p.key === "pro" })),
  ];

  return (
    <div className="max-w-6xl mx-auto px-5 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">For food truck owners</p>
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-2">Plans that grow your truck</h1>
        <p className="text-slate-500 mt-4 text-lg">List free forever. Upgrade for more events, better placement, and corporate leads.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
        {cards.map((p) => {
          const isCurrent = user?.role === "owner" && (myTier || "basic") === p.key;
          return (
            <div key={p.key} className={`relative rounded-3xl border p-8 flex flex-col ${p.highlight ? "border-[#0FB5A6] bg-white gr-shadow-lg lg:-translate-y-4" : "border-teal-100 bg-white"}`} data-testid={`plan-${p.key}`}>
              {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#0FB5A6] text-white text-xs font-bold">Most popular</span>}
              <div className="flex items-center gap-2">
                <span className={`w-10 h-10 rounded-full flex items-center justify-center ${p.key === "premium" ? "bg-[#F2635F]" : p.key === "fleet" ? "bg-slate-900" : p.key === "pro" ? "bg-[#0FB5A6]" : "bg-slate-100"}`}>
                  <Truck className={`w-5 h-5 ${p.key === "basic" ? "text-slate-500" : "text-white"}`} />
                </span>
                <h3 className="font-display text-2xl font-extrabold">{p.name}</h3>
              </div>
              <div className="mt-5">
                <span className="font-display text-5xl font-black">${p.price}{p.key === "fleet" ? "+" : ""}</span>
                <span className="text-slate-400 font-semibold">{p.price === 0 ? " forever" : " /mo"}</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-slate-600">
                    <Check className="w-5 h-5 text-[#0FB5A6] shrink-0 mt-0.5" /> <span className="text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                disabled={isCurrent || loadingKey === p.key}
                onClick={() => p.key === "basic" ? navigate(user ? "/dashboard/owner" : "/register") : upgrade(p.key)}
                className={`mt-8 rounded-full h-12 font-bold active:scale-95 transition-transform ${p.highlight ? "bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
                data-testid={`plan-cta-${p.key}`}
              >
                {isCurrent ? "Current plan" : loadingKey === p.key ? "Redirecting…" : p.key === "basic" ? p.cta : <><CreditCard className="w-4 h-4 mr-2" /> Pay with card</>}
              </Button>
              {p.key !== "basic" && !isCurrent && (
                <Button
                  variant="outline"
                  onClick={() => openPayPal(p.key)}
                  className="mt-2 rounded-full h-11 font-bold border-[#F2635F] text-slate-800 hover:bg-teal-50 active:scale-95 transition-transform"
                  data-testid={`paypal-cta-${p.key}`}
                >
                  Pay with <span className="text-[#003087] font-black ml-1">Pay</span><span className="text-[#009cde] font-black">Pal</span>
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-center text-slate-400 text-sm mt-8">Cancel anytime. Card test: 4242 4242 4242 4242. PayPal: use your sandbox buyer account.</p>

      <Dialog open={!!paypalPlan} onOpenChange={(o) => !o && setPaypalPlan(null)}>
        <DialogContent data-testid="paypal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display capitalize">
              Subscribe to {paypalPlan} with PayPal
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-2">
            Recurring monthly subscription for the <b className="capitalize">{paypalPlan}</b> plan
            {paypalPlan && plans ? ` · $${plans.plans.find((p) => p.key === paypalPlan)?.price}/mo` : ""}. Billed automatically each month; cancel anytime in PayPal.
          </p>
          {paypalPlan && <PayPalCheckout plan={paypalPlan} onSuccess={onPaypalSuccess} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
